import { useEffect, useRef } from "react";
import { IconClockHour4 } from "@tabler/icons-react";
import { useApprovalStatus } from "../../hooks/useApprovalStatus";

/**
 * Top-of-app strip shown to buyers whose documents are pending admin approval.
 * Hidden for admins, sellers, and fully-approved buyers. Buyers without docs
 * uploaded never reach the app — `OnboardingGuard` redirects them to the
 * documents step of the wizard.
 *
 * Sets --approval-banner-h on :root so fixed overlays (e.g. the AI chat panel)
 * can offset their top edge below this banner.
 */
export function ApprovalBanner() {
  const { status, isLoading } = useApprovalStatus();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      document.documentElement.style.setProperty("--approval-banner-h", "0px");
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        "--approval-banner-h",
        `${entry.contentRect.height}px`,
      );
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--approval-banner-h", "0px");
    };
  }, [status]);

  if (isLoading || status !== "pending") return null;

  return (
    <div ref={ref} className="relative z-[80] flex w-full items-center justify-center gap-3 bg-bgLight px-4 py-2 text-textBody print:hidden">
      <IconClockHour4 size={18} className="shrink-0" />
      <span className="text-sm">
        Documents under review — usually 1–2 business days. You have limited
        access (prices hidden, 3 trial messages per chat) until approval.
      </span>
    </div>
  );
}
